/**
 * IndustryAssignmentsDrawer — the "which companies hold this industry, and move
 * them so it can be deleted" panel.
 *
 * An industry can only be deleted at ZERO assignments (companies.industry
 * resolves by its slug). Rather than just telling the user a count, this lists
 * the exact companies, lets them select some/all and reassign them to another
 * industry, and enables deletion once none remain. Reassignment is a plain
 * companies PATCH (industry = target slug); the industry slug itself is never
 * touched (it's locked).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Trash2 } from 'lucide-react';
import { AdminFormDrawer } from '@/components/settings/AdminFormDrawer';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getAdminCompanys, updateAdminCompany, deleteFilterIndustry } from '@/lib/adminApi';
import { toast } from 'react-toastify';
import type { FilterIndustry } from '@/types';

interface AssignedCompany {
  id: string;
  name: string;
  slug: string;
  industry?: string;
}

interface Props {
  /** The industry being managed, or null when the drawer is closed. */
  industry: FilterIndustry | null;
  /** All industries, used to populate the reassignment target picker. */
  allIndustries: FilterIndustry[];
  onClose: () => void;
  /** Called after any reassignment or deletion so the parent can refresh counts. */
  onChanged: () => void;
}

export function IndustryAssignmentsDrawer({ industry, allIndustries, onClose, onChanged }: Props) {
  const [companies, setCompanies] = useState<AssignedCompany[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  // Tracks the slug of the most recent load so a slow/overlapping response for a
  // previously-open industry can't paint stale rows under a new title.
  const reqRef = useRef<string | null>(null);

  const load = useCallback(async (slug: string) => {
    reqRef.current = slug;
    // Reset up front so the first frame for a new industry never shows the prior
    // one's rows or a false "safe to delete" empty state while loading.
    setLoading(true);
    setCompanies([]);
    setSelected(new Set());
    setTarget('');
    try {
      const res = await getAdminCompanys({ industries: [slug] });
      if (reqRef.current !== slug) return; // superseded by a newer load
      setCompanies((res.companies as AssignedCompany[]) || []);
    } catch (err) {
      if (reqRef.current === slug) {
        toast.error(err instanceof Error ? err.message : 'Failed to load companies');
      }
    } finally {
      if (reqRef.current === slug) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (industry?.slug) void load(industry.slug);
  }, [industry, load]);

  // Only ACTIVE industries are valid move targets — reassigning a company to a
  // retired industry would immediately re-populate it (server also guards this).
  const targets = allIndustries.filter(
    (i) => i.slug && i.slug !== industry?.slug && i.is_active !== false,
  );
  const allSelected = companies.length > 0 && selected.size === companies.length;

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(companies.map((c) => c.id)));

  const reassign = async () => {
    if (!industry?.slug || selected.size === 0 || !target) return;
    setBusy(true);
    try {
      const ids = [...selected];
      const results = await Promise.allSettled(
        ids.map((id) => updateAdminCompany(id, { industry: target })),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      const moved = ids.length - failed;
      if (failed === 0) toast.success(`Moved ${moved} ${moved === 1 ? 'company' : 'companies'}`);
      else toast.error(`Moved ${moved} of ${ids.length}; ${failed} failed`);
      onChanged();
      await load(industry.slug);
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!industry) return;
    if (!confirm(`Delete "${industry.name}"? This can't be undone.`)) return;
    setBusy(true);
    try {
      await deleteFilterIndustry(industry.id);
      toast.success('Industry deleted');
      onChanged();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete industry');
    } finally {
      setBusy(false);
    }
  };

  const empty = !loading && companies.length === 0;

  return (
    <AdminFormDrawer
      open={industry !== null}
      onClose={onClose}
      title={industry ? `Companies in “${industry.name}”` : ''}
    >
      {industry && (
        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading companies…
            </div>
          ) : empty ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                No companies are assigned to this industry — it's safe to delete.
              </p>
              <Button variant="destructive" className="w-full" disabled={busy} onClick={del}>
                <Trash2 className="mr-2 h-4 w-4" /> Delete industry
              </Button>
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                {companies.length} {companies.length === 1 ? 'company is' : 'companies are'} assigned.
                Move them to another industry to delete this one.
              </p>

              {/* Reassignment bar */}
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="mb-1 block text-xs text-muted-foreground">Move selected to</label>
                  <Select value={target} onValueChange={setTarget}>
                    <SelectTrigger className="bg-background border-border">
                      <SelectValue placeholder="Choose an industry…" />
                    </SelectTrigger>
                    <SelectContent>
                      {targets.map((t) => (
                        <SelectItem key={t.id} value={t.slug!}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  className="bg-[#8FB560] hover:bg-[#7a9d51] whitespace-nowrap"
                  disabled={busy || selected.size === 0 || !target}
                  onClick={reassign}
                >
                  Move {selected.size > 0 ? `(${selected.size})` : ''}
                </Button>
              </div>

              {/* Select-all */}
              <label className="flex items-center gap-2 border-b border-border pb-2 text-xs font-medium text-muted-foreground">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} /> Select all
              </label>

              {/* Company list */}
              <div className="max-h-[45vh] space-y-1 overflow-y-auto">
                {companies.map((c) => (
                  <label
                    key={c.id}
                    className="flex cursor-pointer items-center gap-3 rounded px-2 py-2 hover:bg-muted/40"
                  >
                    <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggle(c.id)} />
                    <span className="flex-1 text-sm text-foreground">{c.name}</span>
                    <span className="font-mono text-[11px] text-muted-foreground">{c.slug}</span>
                  </label>
                ))}
              </div>

              <p className="text-xs text-muted-foreground">
                Delete is available once every company has been moved off this industry.
              </p>
            </>
          )}
        </div>
      )}
    </AdminFormDrawer>
  );
}
